import type { GoogleLocation } from '@/features/google-location'
import { isGoogleMapsEmbedUrl } from '@/features/google-location/utils/embedUrl'
import { SiteSettings } from '@/features/website-builder/schema/site'
import { defaultSiteSettings } from '@/features/website-builder/templates'
import { RestaurantSettings } from '../schema/settings'

/**
 * The venue facts, in the shape a website document holds them.
 *
 * The only place the two shapes meet. The settings store owns these facts; the
 * document keeps a **snapshot**, for the same reason it snapshots the floor plan's
 * rooms and the menu — a published page has no session to fetch anything with,
 * and an address corrected next month must not silently rewrite a page published
 * last month.
 *
 * `previous` is merged over, not replaced, so everything that belongs to the
 * website survives: the template, the sections, the photos, the copied rooms —
 * and, importantly, translations. The panel edits one language, so writing
 * `tagline` wholesale would delete a German translation somebody had already
 * typed into the builder.
 */
export function toSiteSettings(
  settings: RestaurantSettings,
  previous?: SiteSettings,
  /**
   * The venue's Google place: `null` when it is not connected, `undefined`
   * while that is not known yet, which keeps whatever the document had.
   */
  googleLocation?: GoogleLocation | null
): SiteSettings {
  const { identity, address, contact, hours, contentLanguage } = settings
  // A document that does not exist yet starts from the builder's own defaults,
  // so the template, the sections and their variants are real values rather than
  // an empty object the renderer would have to guess at.
  const base = previous ?? defaultSiteSettings(contentLanguage)

  return {
    ...base,

    name: identity.name,
    tagline: { ...base.tagline, ...identity.tagline },
    description: { ...base.description, ...identity.description },

    /*
      Rejoined with a space: the document has one `street` line, and which side of
      it the building number belongs on is a question about the country, not about
      this app. A venue that has no number keeps a clean street name.
    */
    street: [address.street, address.buildingNumber].filter(Boolean).join(' '),
    postalCode: address.postalCode,
    city: address.city,

    phone: contact.phone,
    email: contact.email,

    hours,

    facebookUrl: contact.facebookUrl || undefined,
    instagramUrl: contact.instagramUrl || undefined,

    googleLocation:
      googleLocation === undefined
        ? base.googleLocation
        : pageLinksOf(googleLocation)
  }
}

/**
 * What a page takes from the venue's Google place. A place Google no longer
 * recognises gives a map of nothing and a review form that fails, so the page
 * takes neither.
 *
 * Each link is checked here rather than left to the document's schema: one bad
 * field there fails the whole document, and the builder would reopen on its
 * defaults. A map URL that is not a Google embed (or none, when the backend has
 * no embed key) leaves the map waiting; a review link that is not a URL drops
 * both.
 */
const pageLinksOf = (
  location: GoogleLocation | null
): SiteSettings['googleLocation'] => {
  if (location?.status !== 'active' || !isHttpsUrl(location.reviewUrl)) {
    return undefined
  }
  return {
    embedUrl: isGoogleMapsEmbedUrl(location.embedUrl) ? location.embedUrl : '',
    reviewUrl: location.reviewUrl
  }
}

const isHttpsUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Whether the document's copy still matches the settings it was taken from.
 *
 * What the "your published page is older than your restaurant details" notice is
 * built on. Compares only the fields this adapter writes, so a template change or
 * a new photo does not read as stale venue facts.
 */
export function siteSnapshotIsStale(
  settings: RestaurantSettings,
  previous?: SiteSettings,
  googleLocation?: GoogleLocation | null
): boolean {
  if (!previous) return false
  const next = toSiteSettings(settings, previous, googleLocation)
  const watched: Array<keyof SiteSettings> = [
    'name',
    'street',
    'postalCode',
    'city',
    'phone',
    'email',
    'hours',
    'facebookUrl',
    'instagramUrl',
    'tagline',
    'description',
    'googleLocation'
  ]
  return watched.some(
    key => JSON.stringify(next[key]) !== JSON.stringify(previous[key])
  )
}
